'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { debounce } from 'lodash-es';
import { useState } from 'react';
import { DataGrid, FilterSearch, FilterSelect, Spinner } from '@repo/components';
import {
	Role,
	taskTemplateSchema,
	TASK_SCOPES,
	TaskTemplate,
	TaskTemplatesApiResponse,
	taskTypes,
} from '@repo/shared/domain';
import { TaskTemplateForm } from '@repo/forms';
import {
	EventEmitter,
	guardedPost,
	guardedPut,
	sortToString,
	stringToSort,
	constructQuery,
} from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useLocalStorage } from '@repo/components/hooks';
import useUpdateQueryParams from 'src/hooks/useUpdateQueryParams';
import { useConfig } from 'src/hooks/useConfig';
import { getRolesForAutocomplete } from 'src/api/admin/roles';
import { getTaskTemplates } from 'src/api/admin/taskTemplates';
interface TasksSearch {
	sort?: string;
	page?: number;
	search?: string;
	type?: string;
	scope?: string;
}

export const Route = createFileRoute('/_admin/config/tasks/')({
	validateSearch: (search: Record<string, unknown>): TasksSearch => ({
		sort: search.sort as string | undefined,
		page: search.page as number | undefined,
		search: search.search as string | undefined,
		type: search.type as string | undefined,
		scope: search.scope as string | undefined,
	}),
	component: TasksPage,
});

function durationToString(duration: string | undefined): string {
	if (!duration) return '';
	// Parse ISO 8601 duration
	const match = duration.match(/P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?)?/);
	if (!match) return duration;

	const parts: string[] = [];
	if (match[1]) parts.push(`${match[1]} year${match[1] !== '1' ? 's' : ''}`);
	if (match[2]) parts.push(`${match[2]} month${match[2] !== '1' ? 's' : ''}`);
	if (match[3]) parts.push(`${match[3]} day${match[3] !== '1' ? 's' : ''}`);
	if (match[4]) parts.push(`${match[4]} hour${match[4] !== '1' ? 's' : ''}`);
	if (match[5]) parts.push(`${match[5]} minute${match[5] !== '1' ? 's' : ''}`);
	if (match[6]) parts.push(`${match[6]} second${match[6] !== '1' ? 's' : ''}`);

	return parts.join(', ') || duration;
}

const columns = [
	{
		field: 'name',
		headerName: 'Name',
		flex: 2,
	},
	{
		field: 'type',
		headerName: 'Type',
		flex: 2,
		valueFormatter: (c: { value: string }) => taskTypes.find((t) => t.id === c.value)?.label,
	},
	{
		field: 'validDuration',
		headerName: 'Valid For',
		flex: 1,
		valueFormatter: (c: { value: string }) => durationToString(c.value),
	},
	{
		field: 'estMinutes',
		headerName: 'Estimated Time',
		flex: 1,
		valueFormatter: (c: { value: number }) => c.value && `${c.value} minutes`,
		sortable: false,
	},
	{
		field: 'scope',
		headerName: 'Access Scope',
		flex: 1,
		valueFormatter: (c: { value: keyof typeof TASK_SCOPES }) => TASK_SCOPES[c.value] || 'Everyone',
	},
];

function TasksPage() {
	const searchParams = Route.useSearch();
	const { sort, page, search, type, scope } = searchParams;
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const updateQueryParams = useUpdateQueryParams();
	const { updateValue } = useLocalStorage<string>('configTasksBackUrl');
	const { config } = useConfig();
	const usesPlayit = config?.featureFlags?.USES_PLAYIT || false;

	const [isFormOpen, setIsFormOpen] = useState(false);
	const [activeTaskTemplate, setActiveTaskTemplate] = useState<TaskTemplate | undefined>();

	useBreadcrumbLevel(0, 'Configuration');
	useBreadcrumbLevel(1, 'Tasks');

	const query = constructQuery({
		sort: sort || 'name:asc',
		page,
		search,
		type,
		scope,
		noPagination: 'false',
	});

	const { data: taskTemplatesData, isLoading: tasksLoading } = useQuery({
		queryKey: ['task-templates', query],
		queryFn: () => getTaskTemplates(query),
	});

	const { data: rolesData } = useQuery({
		queryKey: ['roles-autocomplete'],
		queryFn: getRolesForAutocomplete,
	});

	const taskTemplatesApiResponse = taskTemplatesData || { data: [], count: 0, page: 0, pageSize: 25 };
	const roles: Role[] = (rolesData as unknown as Role[]) || [];

	const handlePaginationModelChange = ({ page: newPage }: { page: number }) => {
		updateQueryParams({ page: newPage });
	};

	const handleSort = ([sortItem]: { field: string; sort: 'asc' | 'desc' }[]) => {
		if (!sortItem) {
			return;
		}
		updateQueryParams({ sort: sortToString(sortItem) });
	};

	const redirectToTaskDetail = (slug: string) => {
		updateValue(window.location.href);
		navigate({ to: '/config/tasks/$slug', params: { slug } });
	};

	const handleSubmit = async (data: TaskTemplate) => {
		if (!data) {
			revalidateTaskTemplates();
		}

		let response: { body: TaskTemplate };
		const method = data.id ? guardedPut : guardedPost;

		try {
			response = await method(`/api/task-templates/${data.id || ''}`, data, {
				schema: taskTemplateSchema,
			});
		} catch (_error) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: `Failed to save task template. Did you enter all data correctly?`,
			});
			return;
		}

		EventEmitter.emit('toast', { type: 'success', message: `Task saved` });

		revalidateTaskTemplates();
		setIsFormOpen(false);
		setActiveTaskTemplate(undefined);

		if (response.body.slug) {
			redirectToTaskDetail(response.body.slug);
		}
	};

	const handleCloseForm = () => {
		setIsFormOpen(false);
		setActiveTaskTemplate(undefined);
	};

	const handleOpenForm = () => {
		setIsFormOpen(true);
	};

	const handleRowClick = ({ row }: { row: TaskTemplate }) => {
		redirectToTaskDetail(row.slug);
	};

	const handleSearchChange = (searchValue: string) => {
		updateQueryParams({ search: searchValue });
	};

	const handleFilterChange = (filterdata: { type?: string; scope?: string }) => {
		updateQueryParams(filterdata);
	};

	const revalidateTaskTemplates = () => {
		queryClient.invalidateQueries({ queryKey: ['task-templates'] });
	};

	return (
		<>
			<div className="flex justify-between p-4 pb-0">
				<div key="search" className="flex gap-2">
					<FilterSearch
						label="Search Task"
						defaultValue={search || ''}
						onChange={debounce((searchInput: string) => {
							handleSearchChange(searchInput);
						}, 350)}
						sx={{ width: '100%', minWidth: 300, flexGrow: 1 }}
					/>
					<FilterSelect
						key="typeFilter"
						listSubHeader="Filter Type"
						options={taskTypes.reduce(
							(acc, { isHidden, isDisabled, id, label }) => {
								const shouldHidePlayit = id === 'playit' && !usesPlayit;
								if (!isHidden && !isDisabled && !shouldHidePlayit) {
									acc.push({ value: id, label });
								}
								return acc;
							},
							[{ value: 'all', label: 'All Types' }] as { value: string; label: string }[],
						)}
						value={type || 'all'}
						onChange={(newType: string) => handleFilterChange({ type: newType })}
						sx={{ minWidth: 120 }}
					/>
					<FilterSelect
						key="scopeFilter"
						listSubHeader="Filter Access Scope"
						options={Object.entries(TASK_SCOPES).reduce(
							(acc, [key, value]) => {
								acc.push({ value: key, label: value || 'Everyone' });
								return acc;
							},
							[{ value: 'all', label: 'All Scopes' }] as { value: string; label: string }[],
						)}
						value={scope || 'all'}
						onChange={(newScope: string) => handleFilterChange({ scope: newScope })}
						sx={{ minWidth: 120 }}
					/>
				</div>
				<Button key="add" onClick={handleOpenForm} variant="outlined" startIcon={<AddIcon />}>
					Add Task
				</Button>
			</div>
			<div className="p-4">
				<Paper variant="outlined">
					<DataGrid
						data={taskTemplatesApiResponse.data}
						page={taskTemplatesApiResponse.page}
						pageSize={taskTemplatesApiResponse.pageSize}
						pageSizeOptions={[taskTemplatesApiResponse.pageSize]}
						pagination
						rowCount={taskTemplatesApiResponse.count}
						columns={columns}
						isLoading={tasksLoading}
						paginationMode="server"
						sortingMode="server"
						sortingOrder={['asc', 'desc']}
						sortModel={[stringToSort(sort || 'name:asc')]}
						onRowClick={handleRowClick}
						onReload={revalidateTaskTemplates}
						onPaginationModelChange={handlePaginationModelChange}
						onSortModelChange={handleSort}
					/>
				</Paper>
			</div>
			<TaskTemplateForm
				isOpen={isFormOpen || !!activeTaskTemplate}
				onSubmit={handleSubmit}
				onClose={handleCloseForm}
				data={activeTaskTemplate || {}}
				roles={roles}
				usesPlayit={usesPlayit}
			/>
		</>
	);
}
